import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import type {
  GrantTraderAccessRequest,
  UpdateTraderAccessExpiryRequest
} from "../../../api/Api";

export const brokerTraderAccessKeys = {
  all: ["brokerTraderAccess"] as const,
  list: (entityId: string, includeRevoked: boolean) =>
    [...brokerTraderAccessKeys.all, "list", entityId, includeRevoked] as const
};

export const useBrokerTraderGrants = (
  entityId: string | undefined,
  includeRevoked: boolean
) => {
  return useQuery({
    queryKey: brokerTraderAccessKeys.list(entityId ?? "", includeRevoked),
    enabled: !!entityId,
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerTraderGrants.list({
        entityId: entityId!,
        includeRevoked
      });
      return response.data;
    }
  });
};

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

const invalidateGrants = (
  queryClient: ReturnType<typeof useQueryClient>,
  entityId: string
) => {
  queryClient.invalidateQueries({
    queryKey: [...brokerTraderAccessKeys.all, "list", entityId]
  });
};

export const useGrantBrokerTraderAccess = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: GrantTraderAccessRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerTraderGrants.grant(body);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateGrants(queryClient, variables.entityId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useRevokeBrokerTraderAccess = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { entityId: string; userObjectId: string }) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerTraderGrants.revoke(vars);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateGrants(queryClient, variables.entityId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useUpdateBrokerTraderAccessExpiry = (
  options?: MutationCallbacks
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateTraderAccessExpiryRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerTraderGrants.updateExpiry(body);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateGrants(queryClient, variables.entityId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};
